import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Truck, Building2, Landmark, Shield, Wrench, BarChart3, 
  Award, ArrowRight, Zap, CheckCircle2
} from 'lucide-react';
import { CTABanner } from '../components/shared/CTABanner';
import { SectionHeader } from '../components/shared/SectionHeader';

const HERO_IMAGE = "https://images.unsplash.com/photo-1751553822264-47ef77e3495f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwzfHxlbGVjdHJpYyUyMHZlaGljbGUlMjBjaGFyZ2luZyUyMHN0YXRpb24lMjBjb21tZXJjaWFsfGVufDB8fHx8MTc3NDExODYzM3ww&ixlib=rb-4.1.0&q=85";

const industries = [
  {
    icon: Truck,
    title: "Fleets & Logistics",
    description: "Design and build high-uptime depot charging for light, medium, and heavy-duty fleets.",
    link: "/solutions#fleet"
  },
  {
    icon: Building2,
    title: "Commercial & Retail",
    description: "EV charging as an amenity and revenue stream for malls, offices, and mixed-use properties.",
    link: "/solutions#commercial"
  },
  {
    icon: Landmark,
    title: "Utilities & Municipalities",
    description: "Grid-aware planning, permitting, and coordination with utility stakeholders.",
    link: "/solutions#municipal"
  }
];

const pillars = [
  {
    icon: Shield,
    title: "Utility-Grade Reliability",
    description: "Engineered to the standards of major utilities and telecoms."
  },
  {
    icon: Wrench,
    title: "Turnkey Delivery",
    description: "From feasibility and design to construction, commissioning, and maintenance."
  },
  {
    icon: BarChart3,
    title: "Data & Monitoring",
    description: "Real-time performance, uptime, and energy analytics."
  },
  {
    icon: Award,
    title: "A Skytec Company",
    description: "Backed by decades of infrastructure experience."
  }
];

const processSteps = [
  { step: "01", title: "Assess", description: "Site, load, utility, and operational needs" },
  { step: "02", title: "Design", description: "Electrical, civil, and network architecture" },
  { step: "03", title: "Build", description: "Construction, commissioning, and testing" },
  { step: "04", title: "Maintain", description: "Monitoring, service, and lifecycle planning" }
];

const featuredProjects = [
  {
    clientType: "National Fleet Operator",
    location: "Texas, USA",
    scope: "40 DC fast chargers, 120 Level 2 ports, utility coordination",
    result: "99.5% uptime, 30% faster deployment vs. industry average",
    image: "https://images.unsplash.com/photo-1739105685005-466d690e9c0e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxlbGVjdHJpYyUyMGJ1cyUyMGZsZWV0JTIwZGVwb3R8ZW58MHx8fHwxNzc0MTE4NjQwfDA&ixlib=rb-4.1.0&q=85"
  },
  {
    clientType: "Commercial Real Estate Group",
    location: "California, USA",
    scope: "CaaS deployment across 25 retail locations",
    result: "35% increase in foot traffic, 4.2-star customer rating",
    image: "https://images.pexels.com/photos/4678065/pexels-photo-4678065.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
  },
  {
    clientType: "City Municipality",
    location: "Colorado, USA",
    scope: "Expandable design from 4 to 12 DCFC",
    result: "Zero downtime since launch, 50% under budget",
    image: "https://images.unsplash.com/photo-1760246964044-1384f71665b9?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxNzV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBjb3Jwb3JhdGUlMjBidWlsZGluZyUyMGV4dGVyaW9yfGVufDB8fHx8MTc3NDExODY0M3ww&ixlib=rb-4.1.0&q=85"
  }
];

const deliverables = [
  "Advanced Engineering & System Design",
  "Turnkey Construction & Commissioning",
  "Charging-as-a-Service (CaaS) — no upfront cost",
  "Smart Charging Technology & Real-Time Monitoring"
];

export const HomePage = () => {
  return (
    <main className="pt-20" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center hero-gradient circuit-pattern overflow-hidden" data-testid="hero-section">
        <div className="container-custom py-20 md:py-32">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Left Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7 }}
            >
              <span className="inline-block text-xs font-bold uppercase tracking-[0.2em] text-[#00A2B5] mb-4">
                Lead the Charge
              </span>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight text-slate-900 mb-6 leading-[1.1]">
                Powering the Future of{' '}
                <span className="gradient-text">Electric Mobility</span>
              </h1>
              <p className="text-lg md:text-xl text-slate-600 mb-8 leading-relaxed max-w-xl">
                Utility-grade design, construction, and maintenance for fleets, utilities, and commercial properties.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="btn-gradient px-8 py-4 rounded-full font-semibold text-lg inline-flex items-center justify-center gap-2 group"
                  data-testid="hero-cta-primary"
                >
                  Request a Proposal
                  <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/projects"
                  className="px-8 py-4 rounded-full font-semibold text-lg border-2 border-slate-300 text-slate-700 hover:border-[#00A2B5] hover:text-[#00A2B5] transition-colors inline-flex items-center justify-center"
                  data-testid="hero-cta-secondary"
                >
                  View Projects
                </Link>
              </div>
            </motion.div>

            {/* Right Image */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="relative"
            >
              <div className="relative rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src={HERO_IMAGE} 
                  alt="EV Charging Infrastructure" 
                  className="w-full h-auto object-cover aspect-[4/3]"
                />
                <div className="absolute inset-0 bg-gradient-to-tr from-[#00A2B5]/20 to-transparent" />
              </div>
              {/* Floating stat card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.5 }}
                className="absolute -bottom-6 -left-6 bg-white rounded-xl shadow-xl p-6 border border-slate-100"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="text-3xl font-black text-slate-900">99.5%</p>
                    <p className="text-sm text-slate-500">Avg. Uptime</p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Are Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="who-we-are-section">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <SectionHeader
                overline="Who We Are"
                title="Technology-Driven EV Infrastructure Engineering"
                subtitle="Skytec EV is a technology-driven engineering firm dedicated to building smarter, faster, and more resilient EV charging infrastructure. From early-stage analysis to full-scale construction and ongoing system operations, we provide complete, end-to-end electrification solutions."
              />
              <ul className="space-y-4">
                {deliverables.map((item, index) => (
                  <motion.li
                    key={item}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-6 h-6 text-[#00C5A5] flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700 text-lg">{item}</span>
                  </motion.li>
                ))}
              </ul>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative"
            >
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                    <p className="text-4xl font-black gradient-text mb-2">500+</p>
                    <p className="text-sm text-slate-600">Sites Deployed</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                    <p className="text-4xl font-black gradient-text mb-2">15+</p>
                    <p className="text-sm text-slate-600">Years Experience</p>
                  </div>
                </div>
                <div className="space-y-4 pt-8">
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                    <p className="text-4xl font-black gradient-text mb-2">50</p>
                    <p className="text-sm text-slate-600">States Covered</p>
                  </div>
                  <div className="bg-slate-50 rounded-xl p-6 border border-slate-100">
                    <p className="text-4xl font-black gradient-text mb-2">24/7</p>
                    <p className="text-sm text-slate-600">Support Available</p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Who We Serve Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="who-we-serve-section">
        <div className="container-custom">
          <SectionHeader
            overline="Industries We Serve"
            title="Who We Serve"
            subtitle="Fleet operators, commercial real estate groups, municipalities, logistics hubs, and public charging site hosts."
            centered
          />

          <div className="grid md:grid-cols-3 gap-8">
            {industries.map((industry, index) => (
              <motion.div
                key={industry.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Link
                  to={industry.link}
                  className="block bg-white rounded-xl p-8 border border-slate-200 card-hover h-full"
                  data-testid={`industry-card-${index}`}
                >
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[#00A2B5]/10 to-[#36D890]/10 flex items-center justify-center mb-6">
                    <industry.icon className="w-7 h-7 text-[#00A2B5]" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">{industry.title}</h3>
                  <p className="text-slate-600 mb-4 leading-relaxed">{industry.description}</p>
                  <span className="inline-flex items-center text-[#00A2B5] font-semibold text-sm group">
                    Learn More
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </span>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Skytec EV Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="why-skytec-section">
        <div className="container-custom">
          <SectionHeader
            overline="Why Choose Us"
            title="Why Skytec EV"
            centered
          />

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center mx-auto mb-6 shadow-lg">
                  <pillar.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{pillar.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{pillar.description}</p>
              </motion.div>
            ))}
          </div>

          {/* Skytec Company Badge */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="mt-12 text-center"
          >
            <div className="inline-flex items-center gap-2 px-6 py-3 bg-slate-50 rounded-full border border-slate-200">
              <span className="w-2 h-2 rounded-full bg-[#00A2B5]" />
              <span className="text-sm font-semibold text-slate-600">A SKYTEC COMPANY</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Featured Projects Section */}
      <section className="py-20 md:py-28 bg-slate-50" data-testid="featured-projects-section">
        <div className="container-custom">
          <SectionHeader
            overline="Our Work"
            title="Featured Projects"
            centered
          />

          <div className="grid md:grid-cols-3 gap-8">
            {featuredProjects.map((project, index) => (
              <motion.div
                key={project.clientType}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white rounded-xl overflow-hidden border border-slate-200 card-hover"
                data-testid={`featured-project-${index}`}
              >
                <div className="relative h-48 overflow-hidden">
                  <img 
                    src={project.image} 
                    alt={project.clientType}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                  <div className="absolute bottom-4 left-4">
                    <span className="px-3 py-1 bg-white/90 rounded-full text-xs font-semibold text-slate-700">
                      {project.location}
                    </span>
                  </div>
                </div>
                <div className="p-6">
                  <p className="text-xs font-bold uppercase tracking-wider text-[#00A2B5] mb-2">
                    {project.clientType}
                  </p>
                  <p className="text-slate-600 text-sm mb-3">{project.scope}</p>
                  <p className="text-slate-900 font-semibold text-sm">{project.result}</p>
                  <Link
                    to="/projects"
                    className="inline-flex items-center text-[#00A2B5] font-semibold text-sm mt-4 group"
                  >
                    View Case Study
                    <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                  </Link>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="text-center mt-12">
            <Link
              to="/projects"
              className="inline-flex items-center px-8 py-4 border-2 border-slate-300 rounded-full font-semibold text-slate-700 hover:border-[#00A2B5] hover:text-[#00A2B5] transition-colors"
              data-testid="view-all-projects"
            >
              View All Projects
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 md:py-28 bg-white" data-testid="process-section">
        <div className="container-custom">
          <SectionHeader
            overline="Our Process"
            title="How We Work"
            centered
          />

          <div className="relative">
            {/* Timeline line - desktop */}
            <div className="hidden md:block absolute top-1/2 left-0 right-0 h-1 bg-gradient-to-r from-[#00A2B5] to-[#36D890] transform -translate-y-1/2 rounded-full" />
            
            <div className="grid md:grid-cols-4 gap-8">
              {processSteps.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.15 }}
                  className="relative text-center"
                >
                  <div className="relative z-10 w-16 h-16 rounded-full bg-gradient-to-br from-[#00A2B5] to-[#36D890] flex items-center justify-center mx-auto mb-6 shadow-lg">
                    <span className="text-white font-black text-lg">{step.step}</span>
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA Banner */}
      <CTABanner />
    </main>
  );
};

export default HomePage;
